import { Link } from 'react-router-dom';

function Stat({ value, label }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {value}
      </div>
      <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{label}</div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-14">
      <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-3 max-w-4xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">{subtitle}</p>
      ) : null}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </div>
      <div className="mt-4 text-sm leading-7 text-[var(--text-secondary)]">{children}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="mt-3 grid gap-2 text-sm text-[var(--text-secondary)]">
      {items.map((b) => (
        <li key={b} className="leading-6">
          {b}
        </li>
      ))}
    </ul>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03]">
      <div className="grid grid-cols-1 gap-px bg-white/10" style={{ gridTemplateColumns: `repeat(${headers.length}, minmax(0, 1fr))` }}>
        {headers.map((h) => (
          <div
            key={h}
            className="bg-[var(--bg-secondary)]/50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]"
          >
            {h}
          </div>
        ))}
        {rows.map((row, idx) =>
          row.map((cell, jdx) => (
            <div key={`${idx}-${jdx}`} className="bg-white/[0.02] px-5 py-4 text-sm text-[var(--text-secondary)]">
              {cell}
            </div>
          )),
        )}
      </div>
    </div>
  );
}

export default function ROI() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_25%,rgba(6,182,212,0.14),transparent_55%),radial-gradient(circle_at_75%_20%,rgba(37,99,235,0.18),transparent_45%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Identity Management Centre of Excellence
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            ROI & TCO Analysis
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Building the Business Case for Identity Investment
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request ROI Workshop
            </Link>
            <Link
              to="/offerings"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              View service catalog
            </Link>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat value="150-300%" label="3-Year ROI" />
            <Stat value="12-18 mo" label="Payback Period" />
            <Stat value="40-60%" label="Help Desk Reduction" />
            <Stat value="70-90%" label="Provisioning Time Saved" />
          </div>
        </div>
      </div>

      <Section
        title="Executive Summary"
        subtitle="Identity management is no longer just a security investment—it's a business enabler that delivers measurable returns through operational efficiency, risk reduction, regulatory compliance, and improved user experiences. Organizations implementing modern identity solutions typically achieve ROI of 150-300% over three years, with payback periods of 12-18 months."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Typical Identity Program Returns">
            <BulletList
              items={[
                "150-300% 3-Year ROI",
                "12-18 months payback period",
                "40-60% help desk reduction",
                "70-90% provisioning time saved",
              ]}
            />
          </Card>
          <Card title="How we build the case">
            <BulletList
              items={[
                "Quantify operational savings and productivity gains",
                "Model risk-adjusted loss reduction from identity attack prevention",
                "Estimate audit, compliance, and regulatory penalty avoidance",
                "Capture business enablement impacts for workforce and Customers",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section
        title="ROI Framework"
        subtitle="Identity investments generate returns across four primary value dimensions. Our ROI framework provides a structured approach to quantifying benefits specific to your organization."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="1. Operational Efficiency">
            Automation and streamlining of identity processes directly reduces operational costs and improves productivity.
          </Card>
          <Card title="2. Risk Reduction & Security">
            Modern identity solutions reduce attack surface, decrease breach likelihood, and improve detection and response.
          </Card>
          <Card title="3. Compliance & Audit Efficiency">
            Automated controls and comprehensive audit trails reduce compliance effort, findings, and penalty exposure.
          </Card>
          <Card title="4. Business Enablement">
            Improved experiences, faster onboarding, and secure collaboration enable growth and velocity.
          </Card>
        </div>
      </Section>

      <Section title="1. Operational Efficiency" subtitle="Automation and streamlining of identity processes directly reduces operational costs and improves productivity.">
        <div className="grid gap-4">
          <Card title="Help Desk Cost Reduction">
            <SimpleTable
              headers={["Metric", "Typical Impact"]}
              rows={[
                ["Password reset calls eliminated", "40-60% reduction"],
                ["Average cost per password reset", "₹500-1,500 per incident"],
                ["Self-service adoption rate", "70-85% of password issues"],
                ["MFA-related support tickets", "50-70% reduction with proper UX"],
                ["Account lockout resolution", "80% automated resolution"],
              ]}
            />
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-sm font-semibold text-[var(--text-primary)]">Calculation Example</div>
              <div className="mt-2 text-sm text-[var(--text-secondary)]">
                5,000 employees × 4 password resets/year × ₹1,000/reset × 50% reduction = ₹1 Crore annual savings
              </div>
            </div>
          </Card>

          <Card title="Provisioning & Deprovisioning Efficiency">
            <SimpleTable
              headers={["Process", "Before → After"]}
              rows={[
                ["New employee provisioning time", "3-5 days → 4-8 hours (70-90% faster)"],
                ["Application access requests", "2-3 days → Minutes (95% faster)"],
                ["Termination processing", "1-2 weeks → Same day (100% compliance)"],
                ["Role changes (transfers)", "1-2 weeks → 1-2 days (80% faster)"],
                ["Contractor onboarding", "1-2 weeks → 1-2 days"],
              ]}
            />
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-sm font-semibold text-[var(--text-primary)]">Calculation Example</div>
              <div className="mt-2 text-sm text-[var(--text-secondary)]">
                1,000 new hires/year × 3 days saved × ₹5,000/day productivity = ₹1.5 Crore productivity gain
              </div>
            </div>
          </Card>

          <Card title="IT Administration Efficiency">
            <SimpleTable
              headers={["Activity", "Time Savings"]}
              rows={[
                ["Access certification campaigns", "60-80% effort reduction"],
                ["Audit evidence collection", "70-90% time saved"],
                ["User access reviews", "50-70% faster completion"],
                ["Application onboarding to SSO", "40-60% faster integration"],
                ["Privileged account management", "50-70% administrative reduction"],
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section title="2. Risk Reduction & Security" subtitle="Identity-related breaches represent a significant and growing risk. Modern identity solutions dramatically reduce attack surface and breach likelihood.">
        <div className="grid gap-4">
          <Card title="Breach Cost Avoidance">
            <SimpleTable
              headers={["Risk Factor", "Industry Data"]}
              rows={[
                ["Average data breach cost (India)", "₹17.9 Crore (IBM 2024)"],
                ["Breaches involving credentials", "61% of all breaches"],
                ["Cost reduction with IAM", "₹1.5-2 Crore per breach"],
                ["Mean time to identify breach", "Reduced by 29% with IAM"],
                ["Breach likelihood reduction", "40-60% with modern IAM"],
              ]}
            />
          </Card>

          <Card title="Specific Risk Mitigations">
            <BulletList
              items={[
                "Orphan Account Elimination: Average organization has 30-40% orphan accounts; each represents breach vector",
                "Excessive Access Reduction: 70% of employees have more access than needed; access certification reduces by 40-60%",
                "Privileged Account Control: Unmanaged privileged accounts reduced by 80-90%",
                "Credential Theft Prevention: MFA blocks 99.9% of automated attacks",
                "Insider Threat Reduction: Real-time monitoring detects 60-80% of insider threats earlier",
              ]}
            />
          </Card>

          <Card title="Risk-Adjusted ROI Calculation">
            <div className="text-sm text-[var(--text-secondary)]">
              Expected Loss Reduction = Breach Probability × Breach Cost × Risk Reduction Factor
            </div>
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-sm font-semibold text-[var(--text-primary)]">Example</div>
              <div className="mt-2 text-sm text-[var(--text-secondary)]">
                15% annual breach probability × ₹17.9 Cr cost × 50% reduction = ₹1.34 Cr annual risk-adjusted savings
              </div>
            </div>
          </Card>
        </div>
      </Section>

      <Section title="3. Compliance & Audit Efficiency" subtitle="Identity management is central to regulatory compliance. Automated controls and comprehensive audit trails dramatically reduce compliance costs and penalty risks.">
        <div className="grid gap-4">
          <Card title="Compliance Cost Impact">
            <SimpleTable
              headers={["Compliance Area", "Impact"]}
              rows={[
                ["Audit preparation time", "60-80% reduction"],
                ["Evidence collection effort", "70-90% automated"],
                ["Audit findings/exceptions", "50-70% fewer findings"],
                ["Remediation costs", "40-60% reduction"],
                ["External audit fees", "20-30% reduction in scope/time"],
              ]}
            />
          </Card>

          <Card title="Regulatory Penalty Avoidance">
            <SimpleTable
              headers={["Regulation", "Potential Penalty", "IAM Controls Required"]}
              rows={[
                ["RBI Framework", "License risk, penalties up to ₹1 Cr+", "Access controls, MFA, audit logs"],
                ["DPDP Act 2023", "Up to ₹250 Crore", "Consent, data access controls"],
                ["PCI-DSS", "₹5-10 Lakhs/month + brand damage", "Strong authentication, access logs"],
                ["SEBI CSCRF", "Trading suspension, penalties", "Privileged access management"],
                ["GDPR (Global ops)", "€20M or 4% revenue", "Access rights, data portability"],
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section title="4. Business Enablement" subtitle="Modern identity solutions enable business growth through improved Customer experiences, faster time-to-market, and secure Partner collaboration.">
        <div className="grid gap-4">
          <Card title="Customer Experience Impact (CIAM)">
            <SimpleTable
              headers={["Metric", "Improvement"]}
              rows={[
                ["Registration abandonment rate", "20-40% reduction with social login"],
                ["Login success rate", "95%+ with passwordless"],
                ["Customer onboarding time", "60-80% faster with eKYC"],
                ["Cart abandonment (authentication)", "15-25% reduction"],
                ["Customer satisfaction (NPS)", "10-20 point improvement"],
              ]}
            />
          </Card>

          <Card title="Workforce Productivity">
            <SimpleTable
              headers={["Impact Area", "Benefit"]}
              rows={[
                ["Time to productivity (new hires)", "1-2 weeks faster with automated provisioning"],
                ["Application access wait time", "Days → Minutes with self-service"],
                ["Login time per day", "5-10 minutes saved with SSO"],
                ["Authentication friction", "80% reduction with passwordless"],
                ["Remote work enablement", "Secure access from anywhere"],
              ]}
            />
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="text-sm font-semibold text-[var(--text-primary)]">Calculation Example</div>
              <div className="mt-2 text-sm text-[var(--text-secondary)]">
                5,000 employees × 5 min/day saved × 250 days × ₹500/hour = ₹52 Lakhs annual productivity gain
              </div>
            </div>
          </Card>
        </div>
      </Section>

      <Section
        title="Total Cost of Ownership (TCO) Framework"
        subtitle="Understanding the complete cost picture is essential for informed investment decisions. Our TCO framework captures all cost components across the identity program lifecycle."
      >
        <div className="grid gap-4">
          <Card title="1. Initial Investment Costs">
            <SimpleTable
              headers={["Cost Category", "Components", "Typical Range"]}
              rows={[
                ["Software Licensing", "Platform licenses, user licenses, module licenses", "30-40% of Year 1"],
                ["Implementation Services", "Design, configuration, integration, testing, deployment", "40-50% of Year 1"],
                ["Infrastructure", "Servers, storage, network (if on-prem); cloud resources", "10-15% of Year 1"],
                ["Training", "Admin training, end-user training, documentation", "5-10% of Year 1"],
                ["Change Management", "Communication, adoption programs, process redesign", "5-10% of Year 1"],
              ]}
            />
          </Card>

          <Card title="2. Ongoing Operational Costs (Annual)">
            <SimpleTable
              headers={["Cost Category", "Components", "Typical Range"]}
              rows={[
                ["Subscription/Maintenance", "SaaS fees or on-prem maintenance (15-22% of license)", "40-50% of annual"],
                ["Operations Staff", "Identity administrators, analysts (1 FTE per 3-5K users)", "25-35% of annual"],
                ["Infrastructure", "Cloud hosting, storage, DR, backups", "10-15% of annual"],
                ["Enhancements", "New integrations, feature additions, customizations", "10-15% of annual"],
                ["Support & Managed Services", "Vendor support, managed services fees", "5-15% of annual"],
              ]}
            />
          </Card>

          <Card title="3. Hidden Costs to Consider">
            <BulletList
              items={[
                "Integration Complexity: Legacy system connectors can add 20-40% to implementation costs",
                "Data Migration: Cleaning and migrating identity data often underestimated",
                "Customization Debt: Heavy customizations increase upgrade costs by 30-50%",
                "Multi-Vendor Complexity: Best-of-breed approaches add integration overhead",
                "Organizational Change: Process changes and adoption challenges",
                "Opportunity Cost: Internal resources diverted during implementation",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section title="TCO Benchmarks by Organization Size" subtitle="Indicative TCO ranges for comprehensive identity programs (IAM + IGA + PAM) based on organization size.">
        <SimpleTable
          headers={["Organization Size", "Year 1 (Initial)", "Years 2-3 (Annual)", "3-Year TCO"]}
          rows={[
            ["Small (1-2K users)", "₹50L - 1 Cr", "₹20-40L", "₹90L - 1.8 Cr"],
            ["Mid-size (2-10K users)", "₹1-3 Cr", "₹40L - 1.2 Cr", "₹1.8 - 5.4 Cr"],
            ["Large (10-50K users)", "₹3-8 Cr", "₹1-3 Cr", "₹5 - 14 Cr"],
            ["Enterprise (50K+ users)", "₹8-20 Cr", "₹3-8 Cr", "₹14 - 36 Cr"],
          ]}
        />
        <div className="mt-4 text-xs text-[var(--text-secondary)]">
          Note: Ranges vary significantly based on complexity, number of applications, regulatory requirements, and deployment model (SaaS vs. on-premises).
        </div>
      </Section>

      <Section title="Deployment Model TCO Comparison">
        <SimpleTable
          headers={["Factor", "SaaS/Cloud", "On-Premises"]}
          rows={[
            ["Initial Investment", "Lower (OpEx model)", "Higher (CapEx heavy)"],
            ["Implementation Time", "Faster (3-6 months typical)", "Longer (6-12+ months)"],
            ["Infrastructure Costs", "Included in subscription", "Separate investment required"],
            ["Upgrade Costs", "Included (automatic)", "Significant (every 2-3 years)"],
            ["Customization", "Limited by platform", "Highly customizable"],
            ["Data Residency", "Vendor dependent", "Full control"],
            ["5-Year TCO", "Typically 15-25% lower", "Higher but predictable"],
            ["Best For", "Standard requirements, rapid deployment", "Complex, highly regulated environments"],
          ]}
        />
      </Section>

      <Section title="ROI Calculation Model" subtitle="Use this framework to calculate projected ROI for your identity investment.">
        <div className="grid gap-4">
          <Card title="Step 1: Calculate Total Benefits (Annual)">
            <SimpleTable
              headers={["Benefit Category", "Calculation", "Your Estimate"]}
              rows={[
                ["A. Help Desk Savings", "Password resets × cost × reduction %", "₹ _______"],
                ["B. Provisioning Efficiency", "New hires × days saved × daily cost", "₹ _______"],
                ["C. IT Admin Efficiency", "FTE hours saved × hourly rate", "₹ _______"],
                ["D. Audit Cost Reduction", "Current audit cost × reduction %", "₹ _______"],
                ["E. Risk-Adjusted Breach Savings", "Probability × cost × risk reduction", "₹ _______"],
                ["F. Productivity Gains", "Users × time saved × value", "₹ _______"],
                ["G. Revenue Impact (CIAM)", "Conversion improvement × revenue", "₹ _______"],
                ["TOTAL ANNUAL BENEFITS (A+B+C+D+E+F+G)", "", "₹ _______"],
              ]}
            />
          </Card>

          <Card title="Step 2: Calculate Total Costs (3-Year)">
            <SimpleTable
              headers={["Cost Category", "Year 1", "Years 2-3"]}
              rows={[
                ["H. Software/Subscription", "₹ _______", "₹ _______"],
                ["I. Implementation Services", "₹ _______", "₹ _______"],
                ["J. Infrastructure", "₹ _______", "₹ _______"],
                ["K. Internal Resources", "₹ _______", "₹ _______"],
                ["L. Training & Change Mgmt", "₹ _______", "₹ _______"],
                ["M. Ongoing Operations", "₹ _______", "₹ _______"],
                ["TOTAL 3-YEAR TCO", "₹ _______", "₹ _______"],
              ]}
            />
          </Card>

          <Card title="Step 3: Calculate ROI">
            <div className="text-sm text-[var(--text-secondary)]">3-Year ROI Formula:</div>
            <div className="mt-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-[var(--text-secondary)]">
              ROI = [(Total Benefits × 3) - Total 3-Year TCO] / Total 3-Year TCO × 100%
            </div>
            <div className="mt-5 text-sm text-[var(--text-secondary)]">Payback Period Formula:</div>
            <div className="mt-2 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-[var(--text-secondary)]">
              Payback Period (months) = Total Initial Investment / (Annual Benefits / 12)
            </div>
          </Card>
        </div>
      </Section>

      <Section title="ROI Case Studies" subtitle="Real-world examples demonstrating identity investment returns across industries.">
        <div className="grid gap-4">
          <Card title="Case Study 1: Large Private Bank">
            <BulletList
              items={[
                "Organization: Private sector bank with 45,000 employees, 2,500 branches",
                "Challenge: Manual provisioning taking 5+ days, 40% orphan accounts, RBI audit findings",
                "Solution: Enterprise IGA + PAM implementation with core banking integration",
                "Investment: ₹8.5 Crore (Year 1) + ₹2.5 Crore/year ongoing",
                "Benefits Achieved: Provisioning 5 days → 4 hours (93% faster); Help desk calls 45% reduction (₹1.2 Cr/year savings); Audit findings 70% reduction; Orphan accounts 40% → 2%; Zero RBI observations on access controls",
                "3-Year ROI: 247% with 14-month payback",
              ]}
            />
          </Card>

          <Card title="Case Study 2: State Government Department">
            <BulletList
              items={[
                "Organization: State government with 80,000 employees across 50+ departments",
                "Challenge: No centralized identity, inter-department access issues, citizen service delays",
                "Solution: Centralized IAM with SSO, inter-department federation, citizen portal integration",
                "Investment: ₹12 Crore (Year 1) + ₹4 Crore/year ongoing",
                "Benefits Achieved: Employee productivity 2 weeks faster onboarding; Citizen service time 40% reduction; IT support tickets 55% reduction; Inter-department collaboration previously impossible → seamless; CAG audit compliance 100%",
                "3-Year ROI: 185% with 18-month payback",
              ]}
            />
          </Card>

          <Card title="Case Study 3: E-Commerce Platform">
            <BulletList
              items={[
                "Organization: Digital-native e-commerce with 50M+ Customers, 5,000 employees",
                "Challenge: High cart abandonment, account takeover fraud, complex multi-cloud access",
                "Solution: CIAM with social login + passwordless, workforce Zero Trust, cloud IAM",
                "Investment: ₹4 Crore (Year 1) + ₹1.5 Crore/year ongoing",
                "Benefits Achieved: Registration conversion +35%; Cart abandonment -22%; Account takeover 90% reduction (₹3 Cr fraud savings); Customer support 40% reduction in login issues; Developer productivity 30% faster app deployments",
                "3-Year ROI: 312% with 11-month payback",
              ]}
            />
          </Card>

          <Card title="Case Study 4: Power Distribution Utility">
            <BulletList
              items={[
                "Organization: State DISCOM with 25,000 employees, 15M consumers, critical OT infrastructure",
                "Challenge: IT/OT security gaps, contractor access risks, CEA compliance requirements",
                "Solution: Converged IT/OT identity with PAM for SCADA, consumer portal CIAM",
                "Investment: ₹6 Crore (Year 1) + ₹2 Crore/year ongoing",
                "Benefits Achieved: OT security incidents 75% reduction; Contractor access time 2 weeks → 1 day; Consumer self-service 60% adoption; Field technician productivity 25% improvement; CEA compliance 100% achieved",
                "3-Year ROI: 198% with 16-month payback",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section title="Building Your Business Case" subtitle="Our COE team can help you develop a customized ROI analysis and business case for your identity investment.">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Our Approach">
            <BulletList
              items={[
                "Discovery Workshop (1-2 days): Understand current state, pain points, and strategic objectives",
                "Cost Baseline Assessment: Document current identity-related costs and inefficiencies",
                "Benefit Modeling: Quantify expected benefits using industry benchmarks and your data",
                "TCO Development: Build comprehensive cost model for recommended solution",
                "ROI Analysis: Calculate projected returns with sensitivity analysis",
                "Executive Presentation: Board-ready business case with financial justification",
              ]}
            />
          </Card>

          <Card title="ROI Quick Assessment">
            <div className="text-sm text-[var(--text-secondary)]">Answer these questions to get a preliminary sense of your ROI potential:</div>
            <BulletList
              items={[
                "How many password reset calls does your help desk handle monthly?",
                "How long does it take to provision a new employee with all required access?",
                "What percentage of terminated employees still have active accounts after 30 days?",
                "How much time does your team spend preparing for access-related audits?",
                "Have you experienced any security incidents related to compromised credentials?",
                "What is your Customer registration abandonment rate?",
                "How many applications require separate logins for your employees?",
              ]}
            />
          </Card>
        </div>
      </Section>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Next Steps
            </h2>
            <p className="mt-4 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
              Contact our Identity COE team to schedule a complimentary ROI assessment workshop. We'll help you build a compelling,
              data-driven business case for your identity modernization initiative.
            </p>
            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-[var(--text-secondary)]">
              "Identity is not a cost center—it's a business enabler that pays for itself."
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Request</div>
            <div className="mt-3 text-xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Request Your ROI Assessment
            </div>
            <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">
              Share your current-state metrics and we'll return a tailored ROI and TCO view aligned to your risk and operating model.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white"
            >
              Request ROI Assessment
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
